import React, { useState } from 'react';
import { 
  Calendar,
  Download,
  Eye,
  Check,
  X,
  Clock,
  TrendingUp,
  FileText
} from 'lucide-react';
import { usePayroll } from '@/hooks/usePayroll';
import { generatePayrollPDF } from '@/utils/generatePayrollPDF';

export const MonthlyReport: React.FC = () => {
  const { employees, getMonthlyReport } = usePayroll();
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [workDays, setWorkDays] = useState<Record<string, any>>({});
  
  const daysInMonth = new Date(
    parseInt(selectedMonth.split('-')[0]), 
    parseInt(selectedMonth.split('-')[1]), 
    0
  ).getDate();

  const weekDays = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'];
  
  const generateCalendar = () => {
    const year = parseInt(selectedMonth.split('-')[0]);
    const month = parseInt(selectedMonth.split('-')[1]) - 1;
    const firstDay = new Date(year, month, 1).getDay();
    const days = [];
    
    // Puste dni na początku
    for (let i = 0; i < (firstDay || 7) - 1; i++) {
      days.push(null);
    }
    
    // Dni miesiąca
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const toggleWorkDay = (employeeId: string, day: number) => {
    setWorkDays(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [day]: !prev[employeeId]?.[day]
      }
    }));
  };

  const getWorkDayCount = (employeeId: string) => {
    return Object.values(workDays[employeeId] || {}).filter(Boolean).length;
  };

  const handleGenerateReport = () => {
    const reportData = employees.map(employee => {
      const days = getWorkDayCount(employee.id);
      const hoursPerDay = 8;
      const totalHours = days * hoursPerDay;
      const monthlyPayment = (employee.baseSalary / 168) * totalHours; // Przybliżone obliczenie
      
      return {
        employee,
        workDays: days,
        totalHours,
        payment: monthlyPayment,
        details: workDays[employee.id] || {}
      };
    });

    generatePayrollPDF(reportData, selectedMonth);
  };

  const calendar = generateCalendar();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Rozliczenie miesięczne</h2>
        <div className="flex items-center gap-4">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
          />
          <button
            onClick={handleGenerateReport}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <FileText className="w-5 h-5" />
            Generuj raport PDF
          </button>
        </div>
      </div>

      {/* Kalendarz dla każdego pracownika */}
      <div className="space-y-6">
        {employees.map(employee => (
          <div key={employee.id} className="bg-zinc-800 rounded-lg p-6 border border-zinc-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {employee.firstName} {employee.lastName}
                </h3>
                <p className="text-sm text-gray-400">
                  Dni robocze: {getWorkDayCount(employee.id)} | 
                  Godziny: {getWorkDayCount(employee.id) * 8}h
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Zaznacz wszystkie dni robocze (Pn-Pt)
                    const newDays: Record<number, boolean> = {};
                    calendar.forEach((day, index) => {
                      if (day && (index % 7 < 5)) {
                        newDays[day] = true;
                      }
                    });
                    setWorkDays(prev => ({
                      ...prev,
                      [employee.id]: newDays
                    }));
                  }}
                  className="px-3 py-1 bg-zinc-700 text-white rounded hover:bg-zinc-600 transition-colors text-sm"
                >
                  Zaznacz dni robocze
                </button>
                <button
                  onClick={() => {
                    setWorkDays(prev => ({
                      ...prev,
                      [employee.id]: {}
                    }));
                  }}
                  className="px-3 py-1 bg-zinc-700 text-white rounded hover:bg-zinc-600 transition-colors text-sm"
                >
                  Wyczyść
                </button>
              </div>
            </div>

            {/* Kalendarz */}
            <div className="grid grid-cols-7 gap-1">
              {/* Nagłówki dni tygodnia */}
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs text-gray-400 font-medium py-2">
                  {day}
                </div>
              ))}
              
              {/* Dni miesiąca */}
              {calendar.map((day, index) => (
                <div key={index} className="aspect-square">
                  {day ? (
                    <button
                      onClick={() => toggleWorkDay(employee.id, day)}
                      className={`w-full h-full rounded flex items-center justify-center text-sm transition-colors ${
                        workDays[employee.id]?.[day]
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : index % 7 >= 5
                          ? 'bg-zinc-900 text-gray-500 hover:bg-zinc-700'
                          : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                      }`}
                    >
                      {day}
                    </button>
                  ) : (
                    <div className="w-full h-full" />
                  )}
                </div>
              ))}
            </div>

            {/* Legenda */}
            <div className="mt-4 flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded" />
                <span className="text-gray-400">Dzień pracy</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-700 rounded" />
                <span className="text-gray-400">Dzień wolny</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-900 rounded" />
                <span className="text-gray-400">Weekend</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {employees.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">Brak pracowników</h3>
          <p className="text-gray-500">Dodaj pracowników w zakładce "Pracownicy"</p>
        </div>
      )}
    </div>
  );
};
